/**
 * GeneratorPhotoPreview — the unit's primary gallery photo as a small preview on screens that
 * show a generator but are not its detail page (gallery blueprint U3b): ticket, recurring
 * schedule, contract equipment, equipment picker, search, map.
 *
 * WHY THE SUMMARY, NOT THE GALLERY QUERY. The server keeps `_opgUnit.gallery` on the generator
 * doc (primary thumb + count), so a list of 40 units costs 40 thumbnails and zero
 * `generatorPhotos` queries. Three ways in, cheapest first:
 *   - `generator`: the page already holds the doc — no read.
 *   - `generatorDocId` + `gallery`: a list row that carries the summary (the generators list
 *     callable projects it) — no read. `gallery={undefined}` on such a row means "no photos".
 *   - `generatorDocId` alone: one cached doc read (shared with any `useGenerator` on the page).
 *
 * Renders NOTHING when the unit has no gallery, the summary is absent, or the read is refused —
 * a preview is decoration, never an error state. The full gallery is fetched only when tapped,
 * and opens in the shared lightbox in place (zoom, swipe, thumbnail strip) without navigating.
 */
import { useState, type MouseEvent, type RefObject } from 'react';
import { Images } from 'lucide-react';
import { useGenerator, type GeneratorDoc } from '../../../hooks/useGenerator';
import { useGeneratorGallery } from '../../../hooks/fieldService/useGeneratorGallery';
import { useStorageImage } from '../../../hooks/fieldService/useStorageImage';
import { useInViewOnce } from '../../../hooks/core/useInViewOnce';
import { GalleryLightbox } from './GalleryLightbox';
import { readGalleryPreview } from '../../../lib/fieldService/galleryPreview';
import type { GalleryPreviewSummary } from '../../../types/fieldService/generatorPhotos';

const SIZE_CLASSES = {
  xs: 'h-8 w-8',
  sm: 'h-10 w-10',
  md: 'h-16 w-16',
} as const;

interface GeneratorPhotoPreviewProps {
  /** The page's own generator doc, when it has one: no extra read. */
  generator?: GeneratorDoc | null;
  /** The generator's Firestore doc id, when that is all the page knows. */
  generatorDocId?: string | null;
  /**
   * The summary, when the caller's row already carries it (list surfaces). Passing the prop at
   * all — even as undefined — means "do not read the doc". Pair with `generatorDocId`.
   */
  gallery?: GalleryPreviewSummary | null;
  /** The unit's Generator ID for the image's alt text, when `generator` is not passed. */
  label?: string;
  size?: keyof typeof SIZE_CLASSES;
  className?: string;
  /**
   * False: a display-only thumbnail, no lightbox. For surfaces that are themselves a modal
   * that closes on an outside click or Escape (the search palette), where a lightbox opened
   * from inside would be torn down with it. Selecting the row there opens the unit instead.
   */
  interactive?: boolean;
  /**
   * Download the thumbnail only once the preview scrolls into view (long lists such as the
   * equipment picker). Needs the summary on first render (`generator` or `gallery`): the
   * visibility check attaches once, on mount.
   */
  lazy?: boolean;
  /** Show and edit drawings in the viewer (office/tech hosts only; customer isolation is opt-in). Default false. */
  showMarkup?: boolean;
}

export function GeneratorPhotoPreview(props: GeneratorPhotoPreviewProps) {
  const { generator, generatorDocId, size = 'sm', className = '', interactive = true, lazy = false, showMarkup = false } = props;
  const docId = generator?.id ?? generatorDocId ?? undefined;
  const summaryKnown = !!generator || 'gallery' in props;
  const fetched = useGenerator(summaryKnown ? undefined : docId);
  const doc = generator ?? fetched.data;
  // A refused re-read hides the preview even over a stale cached copy: access was removed.
  const summary = generator
    ? readGalleryPreview(generator)
    : 'gallery' in props
      ? (props.gallery ?? null)
      : fetched.error
        ? null
        : readGalleryPreview(fetched.data);
  const [open, setOpen] = useState(false);
  const { ref, inView } = useInViewOnce<HTMLElement>();
  const image = useStorageImage(summary && (!lazy || inView) ? summary.primaryThumbPath : null, {
    retention: 'short',
  });

  // A refused or failed image read hides the preview entirely: decoration, never an error box.
  if (!docId || !summary || image.error) return null;

  const label = props.label || doc?.['Generator ID'] || 'Unit';
  const countText = `View ${summary.count} gallery ${summary.count === 1 ? 'photo' : 'photos'}`;

  const handleOpen = (e: MouseEvent) => {
    // Previews sit inside clickable rows (picker, search, contract lines): the tap is ours.
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const alt = `${label} gallery photo`;
  const thumb = (zoom: boolean) =>
    image.url ? (
      <img
        src={image.url}
        alt={alt}
        draggable={false}
        className={`h-full w-full object-cover ${zoom ? 'transition-transform duration-200 group-hover:scale-105' : ''}`}
      />
    ) : (
      <span
        className="block h-full w-full animate-pulse bg-slate-200"
        data-testid="gallery-image-loading"
        aria-hidden="true"
      />
    );

  const frame = `relative shrink-0 overflow-hidden rounded-xl ring-1 ring-slate-200 ${SIZE_CLASSES[size]} ${className}`;
  const badge =
    summary.count > 1 ? (
      <span
        aria-hidden="true"
        className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5 rounded-md bg-slate-900/70 px-1 text-[10px] font-semibold leading-4 text-white"
      >
        <Images className="h-2.5 w-2.5" />
        {summary.count}
      </span>
    ) : null;

  if (!interactive) {
    return (
      <span
        ref={ref as RefObject<HTMLSpanElement>}
        className={`block ${frame}`}
        data-testid="generator-photo-preview-static"
      >
        {thumb(false)}
        {badge}
      </span>
    );
  }

  return (
    <>
      <button
        ref={ref as RefObject<HTMLButtonElement>}
        type="button"
        onClick={handleOpen}
        aria-label={countText}
        title={countText}
        className={`group transition hover:ring-[#3FA5FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007ACC] ${frame}`}
      >
        {thumb(true)}
        {badge}
      </button>
      {open && (
        <PreviewLightbox
          // The doc the lookup resolved: a caller may hold the business Generator ID, which
          // useGenerator accepts but generatorPhotos is not keyed by.
          generatorDocId={doc?.id ?? docId}
          primaryPhotoId={summary.primaryPhotoId}
          onClose={() => setOpen(false)}
          showMarkup={showMarkup}
        />
      )}
    </>
  );
}

interface PreviewLightboxProps {
  generatorDocId: string;
  primaryPhotoId: string | null;
  onClose: () => void;
  showMarkup: boolean;
}

/**
 * Mounted only after a tap, so the full gallery query never runs for rows nobody opens.
 * The shared viewer renders itself at the top of the page and owns Escape (closing only
 * itself, and only while it is the top-most dialog), so previews inside the map panel or a
 * modal need nothing extra here (#5442).
 */
function PreviewLightbox({ generatorDocId, primaryPhotoId, onClose, showMarkup }: PreviewLightboxProps) {
  const { data } = useGeneratorGallery(generatorDocId, primaryPhotoId);
  if (!data || data.length === 0) return null;
  return <GalleryLightbox photos={data} initialIndex={0} onClose={onClose} showMarkup={showMarkup} />;
}
